// 면접자 전체 리스트 모달 — 스케줄 모달 위에 한 겹 더 (최근 2개월 + 예정). 행 클릭 → 평가지 모달.
// 데이터는 스케줄 모달이 /dashboard/interview/list 로 받아서 넘겨줌 (평가지 수정이 바로 반영되도록)
import React, { useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import styles from '../../componentsStyle/interviewschedule.module.css';
import { InterviewItem, fmtListDate, fmtTime, gradeStr, hasGrades, parseKey, shortLocation } from './interviewconstants';

const listBoxStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    // 검색 결과 수에 따라 팝업 높이가 바뀌지 않도록 높이 고정, 표 영역만 스크롤
    width: 'calc(100% - 48px)', maxWidth: 1240, height: 'calc(100vh - 48px)', overflow: 'hidden',
    bgcolor: '#fff', borderRadius: '16px', boxShadow: 24, outline: 'none',
    p: '18px 20px 20px', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box'
};

const BASE_COLS = "110px 80px 90px 80px 150px 70px";
const BASE_MIN_W = 580;
const EVAL_COL_W = 172;

interface Props {
    open: boolean;
    items: InterviewItem[];
    since: string;   // YYYY-MM-DD (서버 기준 오늘 − 2개월)
    onOpen: (id: number) => void;
    onClose: () => void;
}

const InterviewListModal: React.FC<Props> = ({ open, items, since, onOpen, onClose }) => {

    const [query, setQuery] = useState("");

    // 이름·지점·근무 중 하나라도 부분 일치
    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((it) => [it.name, it.location, it.job].some((v) => (v || "").toLowerCase().indexOf(q) >= 0));
    }, [items, query]);

    // 평가자 열: 기본 3, 4명 이상 평가한 면접자가 있으면 그만큼
    const evalCount = useMemo(() => Math.max(3, ...rows.map((it) => it.evaluations.filter(hasGrades).length)), [rows]);
    const gridCols = `${BASE_COLS} repeat(${evalCount}, minmax(${EVAL_COL_W - 24}px, 1fr))`;
    const minWidth = BASE_MIN_W + evalCount * EVAL_COL_W;

    const sinceLabel = since ? (() => { const d = parseKey(since); return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`; })() : "";

    return (
        <Modal open={open} onClose={onClose} sx={{ zIndex: 650 }}>
            <Box sx={listBoxStyle} className={styles.wrap} role="dialog" aria-label="면접자 리스트">
                <div className={styles.listHeadRow}>
                    <div>
                        <h2 className={styles.title}>면접자 리스트</h2>
                        <div className={styles.listSub}>{sinceLabel} 이후 (최근 2개월 + 예정) · {rows.length}명</div>
                    </div>
                    <div className={styles.titleRow}>
                        <div className={styles.listSearchWrap}>
                            <svg className={styles.listSearchIcon} aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#777777" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"></circle><path d="M20 20l-3.5-3.5"></path></svg>
                            <input type="text" className={styles.listSearch} value={query} onChange={(e) => setQuery(e.target.value)}
                                placeholder="이름, 지점, 근무 검색" aria-label="이름, 지점, 근무 검색" />
                        </div>
                        <button className={styles.circleBtn} aria-label="닫기" onClick={onClose}>
                            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333333" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                        </button>
                    </div>
                </div>

                <div className={styles.listScroll}>
                    <div style={{ minWidth }}>
                        <div className={styles.listHeadGrid} style={{ gridTemplateColumns: gridCols }}>
                            <div>날짜</div><div>시각</div><div>이름</div><div>지점</div><div>근무</div><div>상태</div>
                            {Array.from({ length: evalCount }).map((_, i) => <div key={i}>평가자 {i + 1}</div>)}
                        </div>
                        {rows.map((it) => {
                            const evs = it.evaluations.filter(hasGrades);
                            return (
                                <button key={it.id} className={styles.listRow} style={{ gridTemplateColumns: gridCols }} onClick={() => onOpen(it.id)}>
                                    <div className={styles.listCell}>{fmtListDate(it.date)}</div>
                                    <div className={styles.listCell}>{fmtTime(it.time)}</div>
                                    <div className={styles.listName}>{it.name}</div>
                                    <div className={styles.listLoc}>{shortLocation(it.location)}</div>
                                    <div className={styles.listCell}>{it.job}</div>
                                    <div className={styles.listCell}>
                                        {it.status === 0 ? <span className={styles.badgePlanned}>예정</span> : null}
                                        {it.status === 1 ? <span className={styles.badgeDone}>완료</span> : null}
                                        {it.status === 2 ? <span className={styles.badgeNoShow}>노쇼</span> : null}
                                    </div>
                                    {Array.from({ length: evalCount }).map((_, i) => {
                                        const ev = evs[i];
                                        return (
                                            <div key={i} className={styles.listEvalCell}>
                                                {ev ? (
                                                    <>
                                                        <span className={styles.gradeLine}>{gradeStr(ev)}</span>
                                                        <span className={styles.listEvalWho}>{ev.evaluatorName}</span>
                                                    </>
                                                ) : <span className={styles.listNone}>-</span>}
                                            </div>
                                        );
                                    })}
                                </button>
                            );
                        })}
                        {rows.length === 0 ? <div className={styles.listEmpty}>검색 결과가 없습니다.</div> : null}
                    </div>
                </div>
                <div className={styles.hint}>행을 누르면 평가지가 열립니다. 평가자가 4명 이상이면 열이 더 늘어납니다.</div>
            </Box>
        </Modal>
    );
};

export default InterviewListModal;
